import { useMemo, useState } from "react";

import { SearchIcon } from "../../assets/Icons";
import { Button } from "../../components/Reusable/Button";
import { StatusSwitch } from "../../components/Reusable/ToggleSwitch";
import { FloatingLabelSelect, LabelInput } from "../../components/Reusable/Layout";
import { ModalContainer } from "../../components/PageComponents";
import { CloseIcon } from "../../icons/promotions";
import {
  SettingItem,
  SettingPayload,
} from "../../features/auth/store/types/setting-types";

export const SettingsTable = ({
  items,
  loading,
  onEdit,
  onDelete,
  onToggleValue,
  onToggleVisible,
  updatingSettingIds,
}: {
  items: SettingItem[];
  loading: boolean;
  onEdit: (setting: SettingItem) => void;
  onDelete: (setting: SettingItem) => void;
  onToggleValue: (setting: SettingItem) => void;
  onToggleVisible: (setting: SettingItem) => void;
  updatingSettingIds: number[];
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(
    () => items.slice(indexOfFirstItem, indexOfLastItem),
    [items, indexOfFirstItem, indexOfLastItem]
  );
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden font-satoshi">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left ">Settings</th>
              <th className="px-6 text-left ">Value</th>
              <th className="px-6 text-left">Visible to User</th>
              <th className="px-6 text-left"></th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {loading ? (
              <tr className="border-b border-gray-200 h-[80px]">
                <td colSpan={4} className="px-6 text-center">
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : currentItems.length > 0 ? (
              currentItems.map((data) => (
                <tr key={data.id} className="border-b border-gray-200 h-[50px]">
                  <td className="px-6 ">{data.setting}</td>
                  <td className="px-6 ">
                    {["on", "off"].includes((data.value || "").toLowerCase()) ? (
                      <StatusSwitch
                        checked={data.value.toLowerCase() === "on"}
                        disabled={updatingSettingIds.includes(data.id)}
                        onChange={() => onToggleValue(data)}
                      />
                    ) : (
                      data.value || "-"
                    )}
                  </td>
                  <td className="px-6">
                    <StatusSwitch
                      checked={data.visible === "on"}
                      disabled={updatingSettingIds.includes(data.id)}
                      onChange={() => onToggleVisible(data)}
                    />
                  </td>
                  <td className="px-6">
                    <div className="flex items-center gap-4 text-blue-400">
                      <button type="button" onClick={() => onEdit(data)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-400"
                        onClick={() => onDelete(data)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-200 h-[50px]">
                <td colSpan={4} className="px-6 text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === totalPages || totalPages === 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          onClick={goToNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const SettingsSearchFilter = ({
  search,
  onSearchChange,
  onClick,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  onClick: () => void;
}) => {
  return (
    <div className="flex flex-col bp:flex-row bp:items-center bp:justify-between mb-4 gap-2">
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search settings..."
          className="border p-2 pl-10 rounded-3xl w-[260px] h-[40px] bp:h-[48px] bp:w-[645px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>
      <div>
        <Button
          onClick={onClick}
          bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
          textColor="text-white"
          borderRadius="rounded-lg"
          className="w-[274px] h-[48px] ml-6"
        >
          <div className="flex items-center gap-2">
            <span className="ml-2 text-lgh">+</span>
            <p>ADD SETTINGS</p>
          </div>
        </Button>
      </div>
    </div>
  );
};

export const AddMarchantModal = ({
  onClose,
  isOpen,
  form,
  saving,
  onChange,
  onSubmit,
  isEditing,
}: {
  onClose: () => void;
  isOpen: boolean;
  form: SettingPayload;
  saving: boolean;
  onChange: (field: keyof SettingPayload, value: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isEditing: boolean;
}) => {
  if (!isOpen) return null;
  return (
    <ModalContainer
      title={isEditing ? "Edit Settings" : "Add Merchant Settings"}
      closeIcon={
        <button onClick={onClose}>
          <CloseIcon />
        </button>
      }
    >
      <form className="space-y-4 mt-2" onSubmit={onSubmit}>
        <div>
          <LabelInput
            type="text"
            value={form.setting}
            placeholder="Setting"
            name="setting"
            onChange={(e) => onChange("setting", e.target.value)}
            required
          />
        </div>
        <div>
          <LabelInput
            type="text"
            value={form.value}
            placeholder="Default Value"
            name="value"
            onChange={(e) => onChange("value", e.target.value)}
          />
        </div>
        <div>
          <FloatingLabelSelect
            value={form.visible}
            placeholder="Visible to User"
            name="visible"
            onChange={(e) => onChange("visible", e.target.value)}
            options={[
              { value: "on", label: "On" },
              { value: "off", label: "Off" },
            ]}
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
            textColor="text-white"
            borderRadius="rounded-lg"
            className="w-[250px] h-[48px] ml-6"
          >
            {saving
              ? "SAVING..."
              : isEditing
              ? "UPDATE SETTINGS"
              : "ADD MERCHANT SETTINGS"}
          </Button>
        </div>
      </form>
    </ModalContainer>
  );
};
